import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Plus, Route, FileSpreadsheet } from "lucide-react";
import { TspBreadcrumb, TspEmptyState } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import notification from "../../../../helpers/notification";
import { LIST_STATUS_FILTERS } from "../../constants/tspJourneyStatus";
import useTspJourneysListData, { emptyJourneysMessage } from "./useTspJourneysListData";
import downloadJourneysExport, { exportErrorMessage } from "./downloadJourneysExport";
import JourneyFilters from "./components/JourneyFilters";
import PastJourneysTable, { PastJourneysTitle } from "./components/PastJourneysTable";

const NO_OPTIONS = { teams: [], states: [], temples: [], years: [] };

export default function TspJourneyListPage() {
  const { places, summary, filters, applyFilters, setPage } = useTspJourneysListData();
  const summaryData = summary.data;
  const [exporting, setExporting] = useState(false);

  // Every journey matching the APPLIED filters (all pages), as Excel.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadJourneysExport(filters);
    } catch (error) {
      notification("error", await exportErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };
  const nothingToExport = !places.data || places.data.length === 0;

  const renderList = () => {
    if (places.error) return <TspLoadError error={places.error} onRetry={places.reload} what="journey places" />;
    if (!places.data) return <div className="tsp-page tsp-dashboard-loading">Loading journey places…</div>;
    if (places.data.length === 0) return <TspEmptyState icon={Route} title="No journey places found" message={emptyJourneysMessage(filters)} />;
    return <PastJourneysTable rows={places.data} meta={places.meta} onPageChange={setPage} />;
  };

  return (
    <div className="tsp-page tsp-journeys-page tsp-journey-list-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Journey List</h1>
          <TspBreadcrumb items={["Dashboard", "Journey List"]} />
        </div>
        <Link to="/tsp/journeys/new" className="tsp-btn tsp-btn-success tsp-btn-pill">
          <Plus size={15} /> Create New Journey
        </Link>
      </div>

      <JourneyFilters applied={filters} options={summaryData?.filter_options || NO_OPTIONS} onApply={applyFilters} />

      <div className="tsp-journey-list">
        <div className="tsp-journey-list-head">
          <PastJourneysTitle title={LIST_STATUS_FILTERS[filters.status]?.label || "Journeys"} totals={places.meta?.totals || summaryData?.totals || { completed: 0, cancelled: 0 }} />
          <button
            type="button"
            className="tsp-btn tsp-btn-outline-success tsp-btn-sm tsp-journey-export-btn"
            onClick={handleExport}
            disabled={exporting || nothingToExport}
            title="Download every journey matching the filters as an Excel file"
            aria-label={exporting ? "Exporting…" : "Export to Excel"}
          >
            <FileSpreadsheet size={15} aria-hidden="true" />
            <span className="tsp-journey-export-text">{exporting ? "Exporting…" : "Export to Excel"}</span>
          </button>
        </div>
        {renderList()}
      </div>
      <ToastContainer />
    </div>
  );
}
